import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarDismiss } from '@angular/material/snack-bar';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import introJs from 'intro.js';
import { Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { RootState } from '../root-state.model';
import { AppActions } from './app.actions';
import { AppSelectors } from './app.selectors';
import { BuiltInChecklistService } from '../../features/checklist/services/built-in-checklist.service';
import {
  DependencyTreeContainerComponent,
  DependencyTreeInput,
} from '../../features/dependency/containers/dependency-tree-container/dependency-tree-container.component';
import { ViewerCommand } from '../../features/document-viewer/models/document-viewer.models';
import {
  FeedbackModalComponent,
  FeedbackModalData,
  FeedbackModalResult,
} from '../../features/feedback/components/feedback-modal/feedback-modal.component';
import { FeedbackService } from '../../features/feedback/service/feedback.service';
import { GUIDED_TOURS } from '../../features/guided-tour/constants/guided-tours';
import { HelpComponentMode } from '../../features/help/models/help.model';
import { ActivityEvent } from '../../features/home/models/activity-event.model';
import { ActivityService } from '../../features/home/services/activity.service';
import { ApplicationService } from '../../features/home/services/application.service';
import { EditTemplateModalComponent } from '../../features/market-comparison/components/edit-template-modal/edit-template-modal.component';
import { EditTemplateModalConfig } from '../../features/market-comparison/components/edit-template-modal/template-modal.models';
import {
  CreateDialogData,
  CreateModalComponent,
} from '../../features/shared/components/create-modal/create-modal.component';
import { InfoModalComponent } from '../../features/shared/components/info-modal/info-modal.component';
import {
  NotificationData,
  NotificationComponent,
} from '../../features/shared/components/notification/notification.component';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import {
  WebSocketEntities,
  WebSocketReloadEvent,
} from '../../features/shared/models/web-socket/web-socket.models';
import { DownloadService } from '../../features/shared/services/download.service';
import { TermSynonymOrganizerService } from '../../features/term-synonym-organizer/services/term-synonym-organizer.service';
import { UpdateService } from '../../features/shared/services/update.service';
import { createPersonalWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DocumentViewerActions } from '../document-viewer/document-viewer.actions';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class AppEffects {
  private breakpointObserver = inject(BreakpointObserver);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly title = inject(Title);
  private readonly appService = inject(ApplicationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly downloadService = inject(DownloadService);
  private readonly activityService = inject(ActivityService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly webSocketService = inject(WebSocketService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly updateService = inject(UpdateService);
  private readonly synonymService = inject(TermSynonymOrganizerService);
  private readonly builtInChecklistService = inject(BuiltInChecklistService);

  private readonly notifications: PushNotificationRecord = {
    [AppActions.SendFeedbackSuccess.type]: { message: 'Feedback sent', isSuccess: true },
    [AppActions.SendFeedbackFail.type]: {
      message: 'Failed to send the feedback',
      isSuccess: false,
    },
  };

  private readonly templateModalId = 'teamplate-entity-edit-modal';

  startIntro$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.StartGuidedTour),
        map((action) => ({
          tourId: action.guidedTourId,
          steps: GUIDED_TOURS.find((tour) => tour.id === action.guidedTourId)?.steps || [],
        })),
        filter(({ tourId, steps }) => !!steps.length),
        delay(0),
        map(({ tourId, steps }) => {
          // stop any guided tour in the viewer
          this.store.dispatch(
            DocumentViewerActions.SendCommand({
              message: {
                commandType: ViewerCommand.StopGuidedTour,
                value: null,
              },
            })
          );

          // start the new guided tour
          const introInstance = introJs();
          introInstance
            .setOptions({
              buttonClass: 'mat-raised-button mat-button-base mat-accent',
              tooltipClass: 'semeris-guided-tour',
              showBullets: false,
              showStepNumbers: true,
              disableInteraction: true,
              doneLabel: 'Finish Guided Tour',
              nextLabel: 'Continue',
              prevLabel: 'Back',
              steps,
            })
            .onafterchange(() => {
              introInstance.refresh(true);
            })
            .oncomplete(() => {
              this.store.dispatch(
                UserProfileActions.ToggleCompletedTours({ tourId, completed: true })
              );
              this.store.dispatch(
                AppActions.AddActivity({
                  activity: ActivityEvent.GuidedTourComplete,
                  meta: { tourId },
                })
              );
              if (tourId === 'dashboard' && location.pathname === '/home') {
                return;
              }

              this.store.dispatch(
                AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.GuidedTours })
              );
            })
            .onbeforeexit(() => {
              this.router.navigate([], {
                queryParams: {
                  ...this.route.snapshot.queryParams,
                  forcedHover: undefined,
                  guidedTourId: undefined,
                },
              });
              return true;
            })
            .start();
        })
      ),
    { dispatch: false }
  );

  exportAsJson$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ExportAsJson),
        map((action) => {
          this.downloadService.exportAsJson(action.value, action.filename);
        })
      ),
    { dispatch: false }
  );

  logout$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.Logout),
        switchMap((action) =>
          this.activityService.logoutActivity().pipe(
            catchError((err) => {
              console.error(err);
              return of({});
            }),
            tap(() => {
              localStorage.removeItem('semeris-user-email');
              localStorage.removeItem('semeris-user-display-name');
              this.authService
                .logout({
                  logoutParams: {
                    returnTo: `${window.origin}/auth/logout`,
                  },
                })
                .subscribe();
            })
          )
        )
      ),
    { dispatch: false }
  );

  addActivity$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AddActivity),
        switchMap((action) => this.activityService.addActivity(action.activity, action.meta))
      ),
    { dispatch: false }
  );

  openFeedback$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.OpenFeedback),
      withLatestFrom(this.authService.user$),
      switchMap(([action, user]) => {
        const modal = this.dialog.open<
          FeedbackModalComponent,
          FeedbackModalData,
          FeedbackModalResult | boolean
        >(FeedbackModalComponent, {
          panelClass: 'semeris-feedback-modal',
          autoFocus: false,
          width: '700px',
          data: {
            email: user.email,
            subject: action.subject,
            message: action.message,
          },
        });

        return modal.afterClosed().pipe(
          filter((feedback): feedback is FeedbackModalResult => !!feedback),
          map((feedback) => AppActions.SendFeedback({ feedback }))
        );
      })
    )
  );

  sendFeedback$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.SendFeedback),
      switchMap((action) => {
        return this.feedbackService.sendMail(action.feedback).pipe(
          map(() => AppActions.SendFeedbackSuccess()),
          reportEffectError(AppActions.SendFeedbackFail)
        );
      })
    )
  );

  startObserv$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.StartObservLayout),
      exhaustMap(() =>
        this.breakpointObserver.observe([
          Breakpoints.XSmall,
          Breakpoints.Small,
          Breakpoints.Medium,
          Breakpoints.Large,
        ])
      ),
      takeUntil(this.actions$.pipe(ofType(AppActions.StopObservLayout))),
      map((breakpointState) => AppActions.LayoutChanged({ breakpointState }))
    )
  );

  setTitleEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.SetTitle),
        map((action) => {
          this.title.setTitle(action.title);
        })
      ),
    { dispatch: false }
  );

  loadInitData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitData),
      tap(() => {
        console.debug(`[AppActions.LoadInitData] received`);
      }),
      switchMap(() => {
        const start = Date.now();
        console.debug(`[AppActions.LoadInitData] starting getInitialData`);
        return this.appService.getInitialData().pipe(
          tap(() => {
            console.debug(
              `[AppActions.LoadInitData] getInitialData done in ${Date.now() - start} ms`
            );
          }),
          map((data) => {
            console.debug(`[AppActions.LoadInitData] dispatching LoadInitDataSuccess`);
            return AppActions.LoadInitDataSuccess({ data });
          })
        );
      })
    )
  );

  loadBuiltInChecklists$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess, AppActions.LoadBuiltInChecklists),
      switchMap(() =>
        this.builtInChecklistService.getResolved().pipe(
          map((resolved) =>
            AppActions.LoadBuiltInChecklistsSuccess({
              topicIdsByAsset: resolved.reduce<Record<string, string[]>>(
                (acc, pair) => ({ ...acc, [pair.assetShortName]: pair.topicIds }),
                {}
              ),
            })
          ),
          catchError((error) => {
            console.error('[AppEffects] failed to load built-in checklists', error);
            return of(AppActions.LoadBuiltInChecklistsSuccess({ topicIdsByAsset: {} }));
          })
        )
      )
    )
  );

  loadSynonymClasses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess, AppActions.LoadSynonymClasses),
      switchMap(() =>
        this.synonymService.getAllSynonymClasses().pipe(
          map((items) => {
            const synonymClassesByAsset: Record<string, string[][]> = {};
            for (const item of items) {
              synonymClassesByAsset[item.assetShortName] = item.synonymClasses;
            }
            return AppActions.LoadSynonymClassesSuccess({ synonymClassesByAsset });
          }),
          catchError(() => of(AppActions.LoadSynonymClassesSuccess({ synonymClassesByAsset: {} }))),
        ),
      ),
    ),
  );

  pushNotificationEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.PushNotification),
      switchMap((action) => {
        let config: MatSnackBarConfig;

        if (action.config === 'success') {
          config = SUCCESS_NOTIFICATION_CONFIGURATION;
        } else if (action.config === 'fail') {
          config = FAIL_NOTIFICATION_CONFIGURATION;
        } else {
          config = action.config;
        }

        return this.snackBar
          .openFromComponent(NotificationComponent, {
            ...config,
            data: {
              closable: action.closable,
              message: action.message,
              action: action.action,
            } as NotificationData,
          })
          .afterDismissed()
          .pipe(
            take(1),
            filter((dismiss: MatSnackBarDismiss) => dismiss.dismissedByAction),
            map(() => AppActions.PushNotificationAction({ id: action.id }))
          );
      })
    )
  );

  pushNotificationActionEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.PushNotificationAction),
        filter((action) => action.id === 'reload_application'),
        map(() => (window.location.href = '/home'))
      ),
    { dispatch: false }
  );

  reloadTokenActionEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.PushNotificationAction),
      filter((action) => action.id === 'reload_token'),
      map((action) => AppActions.GetNewTokenAndReload())
    )
  );

  getNewTokenAndReloadEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.GetNewTokenAndReload),
        switchMap(() =>
          this.authService
            .getAccessTokenSilently({ cacheMode: 'off' })
            .pipe(map(() => (window.location.href = window.location.href.toString())))
        )
      ),
    { dispatch: false }
  );

  clearPushNotificationEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ClearPushNotification),
        tap(() => {
          this.snackBar.dismiss();
        })
      ),
    { dispatch: false }
  );

  enterOfflineModeEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.EnterOfflineMode),
      withLatestFrom(this.store),
      filter(([action, store]) => !store.app.isOffline),
      map(() => AppActions.EnterOfflineModeSuccess())
    )
  );

  enterOfflineModeSuccessEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.EnterOfflineModeSuccess),
      map(() =>
        AppActions.PushNotification({
          message: 'Your network appears to be down. Please try again shortly.',
          action: 'Reload application',
          id: 'reload_application',
          closable: true,
          config: {
            panelClass: 'error-notification',
            verticalPosition: 'top',
          },
        })
      )
    )
  );

  restoreFromOfflineModeEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.RestoreFromOfflineMode),
      map(() =>
        AppActions.PushNotification({
          message: 'Your network appears to be online again.',
          config: {
            panelClass: 'success-notification',
            verticalPosition: 'top',
            duration: 3_000,
          },
        })
      )
    )
  );

  showInfoModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ShowInfoModal),
        map((action) => this.dialog.open(InfoModalComponent, { data: action.data }))
      ),
    { dispatch: false }
  );

  openCreateModalEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.OpenCreateModal),
      switchMap((action) =>
        this.dialog
          .open<CreateModalComponent, CreateDialogData>(CreateModalComponent, { data: action.data })
          .afterClosed()
          .pipe(
            take(1),
            map((displayName) =>
              AppActions.CreateModalClosed({
                displayName,
                modalId: action.modalId,
                additionalInfo: action.additionalInfo,
              })
            )
          )
      )
    )
  );

  openTemplateModalEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.OpenTemplateModal),
      withLatestFrom(this.store.select(AppSelectors.assetOptions)),
      tap(() => {
        this.closeTemplateModal();
      }),
      switchMap(([action, assetOptions]) =>
        this.dialog
          .open<EditTemplateModalComponent, EditTemplateModalConfig>(EditTemplateModalComponent, {
            id: this.templateModalId,
            data: { ...action.data, assets: assetOptions },
            minHeight: '50vh',
            maxHeight: '80vh',
          })
          .afterClosed()
          .pipe(
            take(1),
            filter((data) => !!data),
            map((data) =>
              AppActions.TemplateModalClosed({
                data,
                modalId: action.modalId,
                additionalInfo: action.additionalInfo,
              })
            )
          )
      )
    )
  );

  openDependencyModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.OpenDependencyModal),
        map((action) =>
          this.dialog.open<DependencyTreeContainerComponent, DependencyTreeInput>(
            DependencyTreeContainerComponent,
            {
              data: {
                id: action.data.id,
                type: action.data.type,
              },
              panelClass: 'semeris-dependency-modal',
              minWidth: '90vw',
              maxWidth: '90vw',
              minHeight: '90vh',
              maxHeight: '90vh',
            }
          )
        )
      ),
    { dispatch: false }
  );

  loadProfileSuccessLoadInitDataEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TriggerAppInit),
      tap(() => {
        console.debug(`[AppActions.TriggerAppInit] received`);
      }),
      map(() => {
        console.debug(`[AppActions.TriggerAppInit] dispatching [AppActions.LoadInitData]`);
        return AppActions.LoadInitData();
      })
    )
  );

  wsEffect$: Observable<Action> = createPersonalWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.App,
    () => {
      return AppActions.PushNotification({
        message: `Your permissions have changed. Reload required.`,
        action: `Reload`,
        closable: false,
        config: { ...WARNING_NOTIFICATION_CONFIGURATION, duration: 0 },
        id: 'reload_token',
      });
    },
    true
  );

  wsResetEffect$: Observable<Action> = createPersonalWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Reset,
    () => {
      this.updateService.hardReset();
      return AppActions.PushNotification({
        message: `The application has been reset due to breaking changes.`,
        config: 'success',
      });
    },
    true
  );

  closeTemplateModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.PushNotificationAction),
        filter((action) =>
          [
            WebSocketReloadEvent.DatapointReload,
            WebSocketReloadEvent.MarketCompareReload,
            WebSocketReloadEvent.FormulaReload,
          ].includes(action.id as WebSocketReloadEvent)
        ),
        map((action) => {
          this.closeTemplateModal();
        })
      ),
    { dispatch: false }
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  private closeTemplateModal() {
    const templateModal = this.dialog.openDialogs.find((d) => d.id === this.templateModalId);
    if (templateModal) {
      templateModal.close();
    }
  }

  redirectOnUnauthorized$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.Unauthorized),
        tap(() =>
          this.router.navigate(['/', SemerisRoutes.unauthorized], {
            queryParams: { from: window.location.href.slice(window.location.origin.length) },
          })
        )
      ),
    { dispatch: false }
  );
}
